import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, Trash2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Small, reusable square photo input. Stores the image as a base64 data-URL
 * (resized client-side to <= 512px / ~80% quality) so the backend can keep
 * the avatar in the user document. No external object storage required.
 */
export default function PhotoPicker({ value, onChange, testIdPrefix = "photo", size = 80 }) {
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    try {
      const dataUrl = await resizeImage(f, 512, 0.82);
      onChange(dataUrl);
    } catch (err) {
      toast.error("Could not read image");
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className="border vsm-border bg-zinc-50 dark:bg-zinc-900 flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size }}
        data-testid={`${testIdPrefix}-preview`}
      >
        {value ? (
          <img src={value} alt="profile" className="w-full h-full object-cover" />
        ) : (
          <UserIcon className="w-8 h-8 vsm-muted" />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-none"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-pick`}
        >
          <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
          {value ? "Change" : "Upload photo"}
        </Button>
        {value && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-none text-red-600"
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}-remove`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function resizeImage(file, maxSize, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read fail"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode fail"));
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
